import { useEffect, useMemo, useState } from "react";
import { useI18n, type StringKey } from "../i18n";
import { loadPeopleAnalytics } from "../data";
import { money } from "../lib/payslip";
import { Breakdown, defaultPeriod, PeriodControls, range, type Period } from "./Dashboard";
import type { Workspace } from "../types";

/* eslint-disable @typescript-eslint/no-explicit-any */
type A = Record<string, any>;
const n = (v: unknown) => (v === null || v === undefined ? "—" : String(v));
const pc = (a: number, b: number) => (b ? `${Math.round((100 * a) / b)}%` : "—");

/** Talent analytics across recruitment, performance, training, engagement and relations for a chosen period. */
export function Analytics({ ws }: { ws: Workspace }) {
  const { t } = useI18n();
  const [period, setPeriod] = useState<Period>(() => defaultPeriod("peoplecore-analytics-period"));
  const { from, to } = useMemo(() => range(period), [period]);
  const [d, setD] = useState<A | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    try { localStorage.setItem("peoplecore-analytics-period", JSON.stringify(period)); } catch { /* storage unavailable */ }
    setBusy(true);
    setError(null);
    loadPeopleAnalytics(ws.membership.company_id, from, to).then((x) => setD(x as A)).catch((e) => setError(e.message)).finally(() => setBusy(false));
  }, [ws.membership.company_id, from, to]); // eslint-disable-line react-hooks/exhaustive-deps
  const has = (k: string) => ws.enabled.has(k);
  const controls = <PeriodControls period={period} setPeriod={setPeriod} shown={d?.period} busy={busy && !!d} />;
  if (!d) return <div className="page">{controls}{error ? <p className="alert danger" role="alert">{error}</p> : <p className="muted">{t("loading")}</p>}</div>;
  const r = d.recruitment, p = d.performance, tr = d.training, e = d.engagement, rel = d.relations;
  const tiles = (title: StringKey, items: [StringKey, string, string?][]) => (
    <section className="kpi-group" aria-label={t(title)}>
      <h2 className="sub-head">{t(title)}</h2>
      <div className="kpis">{items.map(([l, v, sub]) => <div key={l} className="stat"><span className="stat-label">{t(l)}</span><span className="stat-value">{v}</span>{sub && <span className="stat-sub">{sub}</span>}</div>)}</div>
    </section>
  );
  const entries = (o: Record<string, number> | undefined, label: (k: string) => string) => Object.entries(o ?? {}).map(([k, v]) => [label(k), Number(v)] as [string, number]).sort((a, b) => b[1] - a[1]);
  return (
    <div className={`page${busy ? " updating" : ""}`}>
      {controls}
      {error && <p className="alert danger" role="alert">{error}</p>}
      <p className="muted small-note">{t("anHeadcount")}: <strong>{d.headcount}</strong></p>
      {has("recruitment") && tiles("navRecruitment", [
        ["anHires", n(r.hires)], ["anDaysToHire", n(r.avg_days_to_hire)], ["anApplications", n(r.applications)],
        ["anOfferAccept", pc(r.offers_accepted, r.offers), `${r.offers_accepted} / ${r.offers}`],
      ])}
      {has("performance") && tiles("navPerformance", [
        ["anAvgRating", n(p.average), p.cycle ?? undefined], ["anGoals", pc(p.goals_achieved, p.goals_total), `${p.goals_achieved} / ${p.goals_total}`],
      ])}
      {has("training") && tiles("navTraining", [
        ["anTrainingHours", n(tr.hours), d.headcount ? `${(tr.hours / d.headcount).toFixed(1)} ${t("anPerHead")}` : undefined], ["anTrainingSpend", money(tr.spend)],
        ["anCompletions", n(tr.completions)], ["anNoShows", n(tr.no_shows)], ["anExpiring", n(tr.expiring_60d)],
      ])}
      {has("engagement") && tiles("navEngagement", [
        ["anEnps", n(e.enps), e.survey ?? undefined], ["anParticipation", e.participation === null ? "—" : `${e.participation}%`], ["anKudos", n(e.kudos)],
      ])}
      {has("employee_relations") && tiles("navRelations", [["anDisciplinary", n(rel.disciplinary)], ["anGrievances", n(rel.grievances)], ["anOpenCases", n(rel.open)]])}
      <div className="dash-grid">
        {has("recruitment") && Object.keys(r.by_source ?? {}).length > 0 && <Breakdown title={t("anAppsBySource")} rows={entries(r.by_source, (k) => (k === "unknown" ? t("dashUnknown") : k))} />}
        {has("performance") && <Breakdown title={t("anRatingSpread")} rows={["5", "4", "3", "2", "1"].map((k) => [`${k} ★`, Number(p.ratings?.[k] ?? 0)] as [string, number])} />}
        {has("performance") && Object.keys(p.by_department ?? {}).length > 0 && <Breakdown title={t("anRatingByDept")} rows={entries(p.by_department, (k) => k)} />}
        {has("engagement") && Object.keys(e.kudos_by_value ?? {}).length > 0 && <Breakdown title={t("anKudosByValue")} rows={entries(e.kudos_by_value, (k) => t(`kdV_${k}` as StringKey))} />}
        {has("employee_relations") && Object.keys(rel.by_outcome ?? {}).length > 0 && <Breakdown title={t("anCaseOutcomes")} rows={entries(rel.by_outcome, (k) => t(`erOut_${k}` as StringKey))} />}
      </div>
      <p className="muted small-note">{t("anNote")}</p>
    </div>
  );
}
